import React, { useState } from 'react';
import { FaChevronDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import PropTypes from 'prop-types';
import './css/CollapsibleRecentEvents.css';

const ROWS_PER_PAGE = 5;

const CollapsibleRecentEvents = ({ events = [], formatCurrency }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const toggleEventsSection = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Pagination logic
  const totalPages = Math.ceil(events.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const paginatedEvents = events.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Reset to first page when events change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [events]);

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'status-badge completed';
      case 'active':
        return 'status-badge active';
      case 'upcoming':
        return 'status-badge upcoming';
      default:
        return 'status-badge';
    }
  };

  return (
    <section className="recent-events-section">
      <div className="recent-events-header" onClick={toggleEventsSection}>
        <h3>Recent Events Performance</h3>
        <div className="collapse-toggle">
          <span>{isCollapsed ? 'Expand' : 'Collapse'}</span>
          <FaChevronDown className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`} />
        </div>
      </div>
      <div className={`events-table ${isCollapsed ? 'collapsed' : ''}`}>
        <table>
          <thead>
            <tr>
              <th>Event Name</th>
              <th>Date</th>
              <th>Attendance</th>
              <th>Capacity</th>
              <th>Fill Rate</th>
              <th>Revenue</th>
            </tr>
          </thead>
          <tbody>
            {paginatedEvents.length ? (
              paginatedEvents.map((event, index) => {
                const attendance = Number(event?.attendance ?? 0);
                const capacity = Number(event?.capacity ?? 0);
                const fillRate = capacity ? Math.round((attendance / capacity) * 100) : 0;

                return (
                  <tr key={index}>
                    <td className="event-name" data-label="Event">{event?.name ?? 'Unnamed Event'}</td>
                    <td data-label="Date">{event?.date ?? '—'}</td>
                    <td data-label="Attendance">{attendance.toLocaleString()}</td>
                    <td data-label="Capacity">{capacity.toLocaleString()}</td>
                    <td data-label="Fill Rate">
                      <span className="fill-rate">
                        {fillRate}%
                      </span>
                    </td>
                    <td data-label="Revenue">{formatCurrency(event?.revenue ?? 0)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="empty-state">No recent events to display yet.</td>
              </tr>
            )}
          </tbody>
        </table>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pagination-controls">
            <div className="pagination-info">
              Showing {startIndex + 1} to {Math.min(endIndex, events.length)} of {events.length} entries
            </div>
            <div className="pagination-buttons">
              <button
                className="pagination-btn"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                <FaChevronLeft />
                Previous
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button
                className="pagination-btn"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Next
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

CollapsibleRecentEvents.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      date: PropTypes.string,
      status: PropTypes.string,
      attendance: PropTypes.number,
      capacity: PropTypes.number,
      revenue: PropTypes.number,
    })
  ).isRequired,
  formatCurrency: PropTypes.func.isRequired,
};

export default CollapsibleRecentEvents;
